import { queryOptions, useQuery } from '@tanstack/react-query';

import { removeLocale } from 'queries/keys.utils';
import { QueryVariables, StaleTime } from 'queries/types';
import { TICKETS_PREFIX } from 'restful/tickets/constants';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type { TicketInfo } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { getTicketsInfo } from 'services/api/restful/generated/tickets/tickets';

type TicketsInfoQueryData = {
  ticketsInfo: Array<TicketInfo>;
};

const normalizeTicketIds = (ticketIds: string | Array<string>): Array<string> =>
  (Array.isArray(ticketIds) ? ticketIds : [ticketIds]).filter(
    (id) => id.trim() !== '',
  );

export const ticketsInfoQuery = (variables: {
  eventId: string;
  ticketIds: string | Array<string>;
}) =>
  queryOptions<TicketsInfoQueryData, RestApiError>({
    queryKey: [TICKETS_PREFIX, 'info', removeLocale(variables)],
    queryFn: async () => {
      const ticketIds = normalizeTicketIds(variables.ticketIds);
      const { data } = await restCall(() =>
        getTicketsInfo(variables.eventId, { ticketIds }),
      );

      return { ticketsInfo: data };
    },
  });

export const useTicketsInfo = (
  variables: QueryVariables<typeof ticketsInfoQuery>,
) => {
  const { data } = useQuery({
    ...ticketsInfoQuery(variables),
    refetchOnWindowFocus: 'always',
    staleTime: StaleTime.THIRTY_SEC,
    enabled:
      !!variables.eventId && !!normalizeTicketIds(variables.ticketIds).length,
  });

  return {
    info: data?.ticketsInfo,
  };
};
